import { Link } from "react-router-dom";
import { usePageMeta } from "../seo";
import {
  ArrowRightIcon,
  LockIcon,
  ShieldIcon,
  LayersIcon,
  GlobeIcon,
  UsersIcon,
  SparkleIcon,
  CheckIcon,
  MinusIcon,
} from "../components/Icons";
import { GridPattern } from "../components/Patterns";

const securityPoints = [
  {
    Icon: LockIcon,
    title: "Every org's data kept apart",
    body:
      "Every row that belongs to an organization carries its org id. The org comes from the verified session, is checked in middleware on every request, and every database query filters by it.",
  },
  {
    Icon: ShieldIcon,
    title: "Credentials encrypted at rest",
    body:
      "Mailbox passwords and OAuth tokens, WhatsApp, Acefone and store tokens, and AI provider keys are encrypted with AES-GCM before they're stored, using an application key held outside the database. API keys are stored only as a hash.",
  },
  {
    Icon: LayersIcon,
    title: "Separate services and databases",
    body:
      "The backend is eight services. Six own a database that no other service reads; they ask each other over HTTP. A fault in one, such as AI assist, doesn't stop tickets or replies.",
  },
  {
    Icon: UsersIcon,
    title: "Google sign-in and four roles",
    body:
      "Agents sign in with Google; there are no ZopDesk passwords. Owners and admins manage settings, agents work tickets, viewers can only read. Workspace auto-join is optional and off by default.",
  },
  {
    Icon: GlobeIcon,
    title: "Hosted on Google Cloud in the US",
    body:
      "The app, databases and file storage run on Google Cloud in the United States. There's no choice of region yet.",
  },
  {
    Icon: CheckIcon,
    title: "Signed requests to your systems",
    body:
      "Webhooks, the customer data lookup and ticket actions are signed with HMAC-SHA256 and a per-endpoint secret, and only go to public https:// addresses.",
  },
];

const notYet = [
  "SOC 2 or other certifications",
  "Single sign-on (SAML / OIDC)",
  "Audit log",
  "Self-serve data export",
  "Choice of hosting region",
];

export default function Security() {
  usePageMeta("/security");
  return (
    <>
      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="pointer-events-none absolute inset-0 text-white/[0.04]">
          <GridPattern />
        </div>
        <div className="pointer-events-none absolute -top-24 -right-32 h-[28rem] w-[28rem] rounded-full bg-brand-500/25 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 pt-16 pb-12 sm:px-6 sm:pt-20">
          <p className="mb-3 animate-fade-up text-xs font-semibold uppercase tracking-wider text-brand-300">
            Security
          </p>
          <h1
            className="animate-fade-up text-4xl font-semibold tracking-tight text-white sm:text-5xl md:text-6xl"
            style={{ animationDelay: "60ms" }}
          >
            How ZopDesk <br className="hidden sm:block" />
            handles your data.
          </h1>
          <p
            className="mt-6 max-w-3xl animate-fade-up text-lg text-ink-300"
            style={{ animationDelay: "140ms" }}
          >
            A helpdesk holds customer conversations, contact details and the
            credentials for your mailboxes and store. Here's how ZopDesk
            protects them today, and what it doesn't offer yet.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="mb-10 max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
            How it's built
          </p>
          <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            What's in place today.
          </h2>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          {securityPoints.map(({ Icon, title, body }, i) => (
            <div
              key={title}
              className="animate-fade-up rounded-xl border border-ink-200 bg-white p-6 shadow-sm"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-500 text-white shadow-md shadow-brand-500/20">
                <Icon width={20} height={20} />
              </div>
              <h3 className="mb-2 text-base font-semibold text-ink-900">
                {title}
              </h3>
              <p className="text-sm leading-relaxed text-ink-600">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-ink-200 bg-gradient-to-br from-brand-50/60 via-white to-violet-50/50">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
          <div className="grid gap-10 md:grid-cols-[1fr_1.2fr] md:items-start">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
                AI
              </p>
              <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
                AI calls go to your own provider account.
              </h2>
              <p className="mt-4 text-ink-600">
                ZopDesk has no AI account of its own. When an agent asks for a
                draft or a summary, ZopDesk sends that ticket's conversation to
                OpenAI or Anthropic with your org's API key, so your agreement
                with that provider governs how they handle it.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-ink-700">
                {[
                  "Nothing is sent to an AI provider until an agent asks",
                  "Only the ticket's conversation is sent",
                  "ZopDesk keeps token counts for the usage dashboard, not the prompts or answers",
                  "Without a key, AI features are simply off",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                      <SparkleIcon width={12} height={12} />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-ink-200 bg-white p-6 shadow-lg sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                Not available yet
              </p>
              <p className="mt-2 text-sm text-ink-700">
                ZopDesk is in beta. If you need any of these today, ZopDesk
                isn't the right fit yet:
              </p>
              <ul className="mt-4 space-y-2 text-sm">
                {notYet.map((d) => (
                  <li
                    key={d}
                    className="flex items-center gap-2 rounded-lg border border-ink-100 bg-ink-50 px-3 py-2"
                  >
                    <MinusIcon width={14} height={14} className="text-ink-500" />
                    <span className="text-ink-700">{d}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-ink-600">
                To have your organization's data deleted, email us; see the{" "}
                <Link to="/privacy" className="font-semibold text-brand-700 hover:text-brand-600">
                  privacy policy
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl border border-ink-800 bg-ink-900 p-10 text-center shadow-xl sm:p-14">
          <div className="pointer-events-none absolute -top-32 left-1/2 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-brand-500/20 blur-3xl" />
          <div className="relative">
            <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              A security question we didn't answer?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-ink-300">
              Ask us. We'll tell you how it works today, including when the
              answer is "it doesn't yet".
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-md bg-gradient-to-br from-brand-500 to-violet-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition hover:from-brand-400 hover:to-violet-400 hover:shadow-xl hover:shadow-brand-500/40"
              >
                Talk to us
                <ArrowRightIcon
                  width={16}
                  height={16}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
